/**
 * Certificado — la posición de la obra en AWAP 2: dos ejes que no se suman.
 *
 * Tres estados, y cada uno es una pantalla distinta porque pide cosas distintas del autor:
 *   · Sin proyecto: ENTRAR. Elegir desde qué punto de la vida de la obra empieza el registro.
 *     Se fija una vez y no se cambia nunca (§4.3); por eso la pantalla lo dice antes del botón.
 *   · Pendiente de declaración (vía B): FIRMAR la declaración de origen. Bloqueante (§3.2.3).
 *   · Activo: la POSICIÓN. Cobertura (ancla), HAS —MAS en la vía B— (banda, con sus componentes,
 *     sus pesos y lo que faltó),
 *     cuadrante, versiones selladas, y la lista fija de lo que este certificado no acredita.
 *
 * El PDF de siete secciones sale PROVISIONAL hasta que se ancle en verify.humanink.io: el motor lo
 * guarda en la carpeta del libro y lo dice en cada página.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { AMBITOS, anclarCertificado, certificadoAwap, declararAwap, decisionDeFondo, entrarAwap, miles, nombreDe, posicionAwap, profundidadAwap, sellarAwap } from "../api";
import type { Ambito, CertificadoAnclado, CertificadoProvisional, EntryPoint, OpcionOrigen, Posicion, Profundidad as Datos } from "../api";
import { nombreDeRuta } from "../logica";
import Plano from "./Plano";
import Profundidad from "./Profundidad";

/** §4.2 — las anclas, con la condición que las define. El número no se explica: se cumple o no. */
const ENTRADAS: { id: EntryPoint; cobertura: number; et: string; condicion: string }[] = [
  { id: "premisa", cobertura: 100, et: "Premisa", condicion: "No existe ni una palabra de la obra." },
  { id: "biblia", cobertura: 85, et: "Biblia", condicion: "Ya hay premisa o sinopsis escritas fuera de registro." },
  { id: "escaleta", cobertura: 75, et: "Escaleta", condicion: "Ya hay estructura fuera de registro; ninguna prosa." },
  { id: "borrador_parcial", cobertura: 50, et: "Borrador parcial", condicion: "Ya hay prosa, incompleta." },
  { id: "manuscrito", cobertura: 20, et: "Manuscrito", condicion: "Ya hay un manuscrito completo." },
  { id: "fichero", cobertura: 0, et: "Solo el fichero", condicion: "Solo el fichero final; no habrá proceso después." },
];

const OPCIONES: { id: OpcionOrigen; et: string }[] = [
  { id: "autor", et: "Lo escribí yo" },
  { id: "tercero", et: "Lo escribió otra persona" },
  { id: "asistido_ia", et: "Con ayuda de IA" },
  { id: "generado_ia", et: "Generado por IA" },
  { id: "desconocido", et: "Origen desconocido" },
  { id: "no_declara", et: "Prefiero no declarar" },
];

/** Los componentes del HAS, con nombre para el autor. */
const COMPONENTES: Record<string, string> = {
  w1_decisiones_no_literales: "Decisiones sobre trama y personajes",
  w2_precedencia_documental: "Documentos antes de la primera generación",
  w3_seleccion_y_descarte: "Lo que descartaste de lo propuesto",
  w4_texto_humano_puro: "Texto tuyo, sin generación",
  w5_direccion: "Cuánto instruyes por cada palabra generada",
  w6_intervencion_posterior: "Lo que retocaste de lo que te quedaste",
  w7_continuidad_temporal: "Días distintos de trabajo",
};

const fecha = (ts: string) => new Date(ts).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
const corto = (h: string) => h.replace(/^sha256:/, "").slice(0, 12);

export default function Certificado(
  { carpeta, doc, pulso }: { carpeta: string; doc?: string | null; pulso?: number },
) {
  const [pos, setPos] = useState<Posicion | null | undefined>(undefined);   // undefined = cargando
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Entrar
  const [entrada, setEntrada] = useState<EntryPoint>("manuscrito");
  const [titulo, setTitulo] = useState(carpeta ? nombreDeRuta(carpeta) : "");
  const [autor, setAutor] = useState(nombreDe(localStorage.getItem("hi.email")) ?? "");
  // Declarar
  const [texto, setTexto] = useState("");
  const [opciones, setOpciones] = useState<OpcionOrigen[]>([]);
  // El último PDF emitido en esta visita, para enseñar dónde quedó y dejar descargarlo.
  const [pdf, setPdf] = useState<CertificadoProvisional | null>(null);
  // El definitivo, cuando se ancla (§8.3). Y el token de la cuenta, que es quien ancla.
  const [anclado, setAnclado] = useState<CertificadoAnclado | null>(null);
  const { getToken } = useAuth();
  // Una decisión de fondo que el autor anota por su cuenta (AWAP 2 §5.2 w1).
  const [ambitos, setAmbitos] = useState<Ambito[]>([]);
  const [nota, setNota] = useState("");
  const [anotado, setAnotado] = useState<string | null>(null);

  // La profundidad de transformación es solo de la vía B, y se pide aparte porque mide dos ficheros
  // enteros: no puede retrasar la posición, que es lo primero que el autor mira.
  const [prof, setProf] = useState<Datos | null>(null);
  const cargar = useCallback(() => {
    if (!carpeta) return;
    posicionAwap(carpeta, doc).then((p) => {
      setPos(p);
      if (p?.cobertura.track === "transformacion") profundidadAwap(carpeta, doc).then(setProf).catch(() => setProf(null));
      else setProf(null);
    }).catch((e) => setFallo(String(e.message ?? e)));
  }, [carpeta, doc]);
  useEffect(cargar, [cargar, pulso]);

  const hacer = async (fn: () => Promise<unknown>) => {
    setOcupado(true); setFallo(null);
    try { await fn(); cargar(); }
    catch (e) { setFallo((e as { datos?: { mensaje?: string }; message: string }).datos?.mensaje ?? (e as Error).message); }
    finally { setOcupado(false); }
  };

  if (!carpeta) {
    return (
      <section className="carta">
        <h2>Certificado<span className="sub">sin manuscrito</span></h2>
        <p className="vacio">Elige antes la carpeta de tu manuscrito: el registro vive junto al libro.</p>
      </section>
    );
  }
  if (pos === undefined) {
    return (
      <section className="carta">
        <p className="vacio" style={fallo ? { color: "var(--acc)" } : undefined}>{fallo ?? "Leyendo el registro…"}</p>
      </section>
    );
  }

  const elegida = ENTRADAS.find((e) => e.id === entrada)!;
  const necesitaDoc = elegida.cobertura <= 50;

  // ── 1 · Entrar ────────────────────────────────────────────────────────────
  if (pos === null) {
    return (
      <>
        {fallo && <section className="carta"><p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p></section>}
        <section className="carta">
          <h2>Entrar en el registro<span className="sub">una sola vez por obra</span></h2>
          <p className="vacio">
            AWAP certifica lo que observa a partir de hoy. Lo único que decides ahora es <b>desde qué
            punto</b> de la vida de tu obra empieza el registro — y eso <b>se fija una vez y no se cambia
            nunca</b>: un libro que entra con el manuscrito hecho no puede llegar después a «premisa».
            El siguiente libro sí puede entrar antes.
          </p>
          <div className="relevo">
            {ENTRADAS.map((e) => (
              // `.paso` es una rejilla de DOS columnas (cara | qu): el radio va oculto dentro, no
              // como tercer hijo, o se come la primera columna y el texto se estrangula.
              <label key={e.id} className="paso" style={{ cursor: "pointer", opacity: entrada === e.id ? 1 : 0.72 }}>
                <span className="cara" style={entrada === e.id
                  ? { background: "var(--acc)", color: "var(--acc-ink)" }
                  : { background: "var(--sup3)", color: "var(--mut)" }}>{e.cobertura}</span>
                <span className="qu">
                  <input type="radio" name="entrada" hidden checked={entrada === e.id} onChange={() => setEntrada(e.id)} />
                  <b>{e.et}</b><span>{e.condicion}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="dir">
            <input className="escribir" placeholder="Título de la obra" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
            <input className="escribir" placeholder="Autor" value={autor} onChange={(e) => setAutor(e.target.value)} />
          </div>
          {necesitaDoc && (
            <p className="vacio">
              Con prosa previa entras por la <b>vía de transformación</b>: se sella el fichero tal como está
              hoy (V0), se escanea lo que el fichero declara de sí mismo, y tendrás que firmar una
              declaración de origen. {doc
                ? <>Se sellará <b>{nombreDeRuta(doc)}</b>.</>
                : <b>Elige antes el manuscrito en la sección Manuscrito.</b>}
            </p>
          )}
          {!necesitaDoc && (
            <p className="vacio">Sin prosa previa entras por la <b>vía de origen</b>: todo lo que escribas desde hoy queda registrado desde el primer documento.</p>
          )}
          <div className="dir">
            <button className="btn" disabled={ocupado || (necesitaDoc && !doc)}
              onClick={() => hacer(() => entrarAwap({ dir: carpeta, titulo, autor, entry_point: entrada, doc }))}>
              Entrar por «{elegida.et}» · cobertura {elegida.cobertura}
            </button>
          </div>
        </section>
      </>
    );
  }

  // ── 2 · Declarar ──────────────────────────────────────────────────────────
  if (pos.proyecto.estado === "pendiente_declaracion") {
    const alternar = (o: OpcionOrigen) => setOpciones((v) => v.includes(o) ? v.filter((x) => x !== o) : [...v, o]);
    return (
      <>
        {fallo && <section className="carta"><p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p></section>}
        <section className="carta">
          <h2>Declaración de origen<span className="sub">V0 sellado · {pos.baseline ? miles(pos.baseline.palabras ?? 0) : "—"} palabras</span></h2>
          <p className="vacio">
            Tu manuscrito entró hecho. AWAP <b>no puede verificar de dónde viene</b> y no lo va a intentar:
            lo que sí hace es guardar tu declaración, firmada, tal cual la escribas. El certificado la
            citará entre comillas y dirá que no está verificada. Sin ella, el registro no avanza.
          </p>
          {/* La caja la pone `.dir`; un `textarea.escribir` suelto no tiene borde. */}
          <div className="dir">
            <textarea className="escribir" rows={4} style={{ flex: 1, resize: "vertical" }}
              placeholder="Con tus palabras: cómo se escribió este manuscrito antes de hoy."
              value={texto} onChange={(e) => setTexto(e.target.value)} />
          </div>
          {/* Opciones NO excluyentes (§3.2.3): chips que se encienden, en fila con salto. */}
          <div className="miembros" style={{ margin: "10px 0" }}>
            {OPCIONES.map((o) => (
              <label key={o.id} className={"chip" + (opciones.includes(o.id) ? " puesto" : "")} style={{ cursor: "pointer" }}>
                <input type="checkbox" hidden checked={opciones.includes(o.id)} onChange={() => alternar(o.id)} />
                {opciones.includes(o.id) ? "✓ " : ""}{o.et}
              </label>
            ))}
          </div>
          <div className="dir">
            <button className="btn" disabled={ocupado || !texto.trim() || !opciones.length}
              onClick={() => hacer(() => declararAwap({ dir: carpeta, texto, opciones, autor: pos.proyecto.autor }))}>
              Firmar la declaración
            </button>
          </div>
        </section>
      </>
    );
  }

  // ── 3 · La posición ───────────────────────────────────────────────────────
  const has = pos.has;
  const entradaDe = ENTRADAS.find((e) => e.id === pos.cobertura.entry_point);
  return (
    <>
      {pos.proyecto.integridad === "rota" && (
        <section className="carta">
          <h2>El registro ha sido modificado<span className="sub">la firma no cuadra</span></h2>
          <p className="vacio" style={{ color: "var(--acc)" }}>
            Los datos inmutables de este proyecto (punto de entrada, fecha, V0) no coinciden con su firma.
            Ningún certificado emitido desde aquí sería válido.
          </p>
        </section>
      )}
      {fallo && <section className="carta"><p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p></section>}

      {/* EL PLANO, con sus cifras al lado (17-sep, Rais). Antes eran tres tarjetas encima y un dibujo a
          todo lo ancho para un solo punto: mucho lienzo y poco dato. Ahora el dibujo ocupa lo que
          necesita y las cifras hacen de leyenda. Dicen lo que el dibujo no puede decir solo: que son
          medidas de cosas distintas y que no se suman. */}
      <section className="carta">
        <h2>Dónde está tu libro<span className="sub">{pos.cuadrante.id} · {pos.cuadrante.nombre}</span></h2>
        <div className="plano">
          <div className="grafico"><Plano pos={pos} /></div>
          <div className="lado">
          <dl className="datos">
            <div>
              <dt>Eje X · cobertura</dt>
              <dd className="valor">{pos.cobertura.valor}</dd>
              <dd className="detalle">{entradaDe?.et ?? pos.cobertura.entry_point} · desde el {fecha(pos.cobertura.entry_ts)}</dd>
            </div>
            <div>
              {/* §12.4: en la vía B se llama MAS, para que nadie lo compare con el HAS de una obra con origen. */}
              <dt>Eje Y · {pos.eje_y?.sigla ?? "HAS"}</dt>
              <dd className="valor">{has?.valor ?? "—"}</dd>
              <dd className="detalle">{has?.valor != null
                ? `banda ${has.banda} · ${has.ventana.generaciones} generaciones en ${has.ventana.dias} ${has.ventana.dias === 1 ? "día" : "días"}`
                : "sin proceso registrado todavía"}
                {pos.cobertura.track === "transformacion" ? " · no se compara con un HAS" : ""}</dd>
            </div>
            <div>
              <dt>Cuadrante</dt>
              <dd className="valor">{pos.cuadrante.id}</dd>
              <dd className="detalle">{pos.cuadrante.nombre}</dd>
            </div>
          </dl>
          <p className="vacio">
            Los dos ejes miden cosas distintas sobre ventanas distintas: no se suman ni se promedian.
            La frontera en 50 es una convención: un 51 y un 49 no son libros distintos.
          </p>
          </div>
        </div>
      </section>

      {has && (
        <section className="carta">
          <h2>De qué está hecho el {pos.eje_y?.sigla ?? "HAS"}<span className="sub">pesos {has.has_version}</span></h2>
          <div className="notas">
            {Object.entries(has.componentes).map(([k, v]) => (
              <div className="nota" key={k}>
                <span className="et">
                  {COMPONENTES[k] ?? k}
                  {pos.pesos?.[k.slice(0, 2)] != null && ` · ×${String(pos.pesos[k.slice(0, 2)]).replace(".", ",")}`}
                </span>
                <span className="raya"><i style={{ width: `${Math.round(v * 100)}%` }} /></span>
                <b className="mono">{Math.round(v * 100)}</b>
              </div>
            ))}
          </div>
          {has.excluidos.length > 0 && (
            <p className="vacio">
              Sin datos, y por eso fuera del cálculo (no se imputa nada):{" "}
              {has.excluidos.map((k) => COMPONENTES[k] ?? k).join(" · ")}.
            </p>
          )}
          <p className="vacio">
            {has.decisiones.con_decision} decisiones tuyas sobre {has.decisiones.total} propuestas:{" "}
            {has.decisiones.aceptadas} aceptadas · {has.decisiones.editadas} retocadas · {has.decisiones.rechazadas} descartadas.
            Los pesos van con su versión: si cambian, cambia la versión y se ve.
          </p>
        </section>
      )}

      {/* LAS DECISIONES DE FONDO. El componente que más pesa después de tu propio texto, y el único
          que no se puede deducir de nada: mover el final o cambiarle el punto de vista a un capítulo
          no deja en el texto ninguna señal que lo distinga de una corrección. Si no se marca, ese
          peso no actúa — y hasta hoy no había dónde marcarlo. */}
      <section className="carta">
        <h2>Decisiones de fondo<span className="sub">lo que cambia la historia por dentro</span></h2>
        <p className="vacio">
          Cuando cambies algo de la historia por tu cuenta —no una propuesta que te hayan hecho: esas se
          marcan en el chat—, anótalo aquí. Es lo que más pesa en tu puntuación después de tu propio
          texto, y nadie lo puede saber si no lo dices tú.
        </p>
        <div className="adjuntos">
          {AMBITOS.map((a) => (
            <button key={a.id} className={`chip${ambitos.includes(a.id) ? " puesto" : ""}`}
                    onClick={() => setAmbitos((x) => x.includes(a.id) ? x.filter((y) => y !== a.id) : [...x, a.id])}>
              {ambitos.includes(a.id) ? "✓ " : ""}{a.et}
            </button>
          ))}
        </div>
        <div className="dir">
          <input className="escribir" placeholder="Qué cambiaste (opcional; se queda en tu ordenador)"
                 value={nota} onChange={(e) => setNota(e.target.value)} />
          <button className="btn" disabled={ocupado || !ambitos.length}
                  onClick={() => hacer(async () => {
                    await decisionDeFondo({ dir: carpeta, doc, ambitos, nota: nota.trim() || undefined });
                    setAmbitos([]); setNota("");
                    setAnotado("Anotada. Ya cuenta en tu puntuación.");
                  })}>
            Anotar la decisión
          </button>
        </div>
        {anotado && <p className="vacio">{anotado}</p>}
      </section>

      {prof && <Profundidad d={prof} />}

      {pos.baseline && (
        <section className="carta">
          <h2>Origen<span className="sub">vía de transformación · no verificado</span></h2>
          <p className="vacio">
            V0 sellado el {fecha(pos.versiones[0].ts)} · <span className="mono">{corto(pos.baseline.sha256)}</span>
            {pos.baseline.palabras ? ` · ${miles(pos.baseline.palabras)} palabras` : ""}.
          </p>
          {pos.baseline.declaracion && (
            <>
              <blockquote>{pos.baseline.declaracion.texto}</blockquote>
              <p className="vacio">
                {pos.baseline.declaracion.opciones.map((o) => OPCIONES.find((x) => x.id === o)?.et ?? o).join(" · ")} ·
                firmada el {fecha(pos.baseline.declaracion.ts)} · <b>AWAP no la ha verificado ni puede verificarla.</b>
              </p>
            </>
          )}
        </section>
      )}

      <section className="carta">
        <h2>Versiones selladas<span className="sub">{pos.versiones.length}</span></h2>
        <div className="relevo">
          {pos.versiones.map((v) => (
            <div className="paso" key={v.id}>
              <span className="cara">{v.id}</span>
              <span className="qu">
                <b>{v.doc ? nombreDeRuta(v.doc) : "—"}{v.palabras ? ` · ${miles(v.palabras)} palabras` : ""}</b>
                <span className="meta">{fecha(v.ts)} · <span className="mono">{corto(v.sha256)}</span>{v.autor_declarado ? ` · ${v.autor_declarado}` : ""}</span>
              </span>
            </div>
          ))}
        </div>
        <div className="dir">
          <button className="btn fantasma" disabled={ocupado || !doc} onClick={() => doc && hacer(() => sellarAwap({ dir: carpeta, doc }))}>
            Sellar la versión actual{doc ? ` (${nombreDeRuta(doc)})` : ""}
          </button>
        </div>
      </section>

      <section className="carta">
        <h2>Lo que este certificado no acredita<span className="sub">en todas las emisiones</span></h2>
        <ul className="pasos">{pos.no_acredita.map((l) => <li key={l}>{l}</li>)}</ul>
      </section>

      <section className="carta">
        <h2>Certificado en PDF<span className="sub">provisional</span></h2>
        <p className="vacio">
          Las siete secciones, con todo lo de arriba. Lleva <b>PROVISIONAL</b> en cada página: todavía no
          está anclado en verify.humanink.io, así que nadie puede comprobarlo desde fuera y sirve para ver
          cómo quedará, no para acreditar nada ante terceros. Cuando se ancle, este mismo botón emitirá el
          definitivo.
        </p>
        <div className="dir">
          <button className="btn" disabled={ocupado || pos.proyecto.estado !== "activo"}
                  onClick={() => hacer(async () => { setPdf(await certificadoAwap({ dir: carpeta, doc })); })}>
            {pdf ? "Generarlo otra vez" : "Generar el certificado provisional"}
          </button>
          {pdf && (
            <a className="btn fantasma" href={`data:application/pdf;base64,${pdf.pdf_base64}`} download={nombreDeRuta(pdf.ruta)}>
              Descargar
            </a>
          )}
        </div>
        {pdf && (
          <p className="vacio">
            Guardado en tu carpeta: <span className="mono">{pdf.ruta}</span> · {pdf.paginas} páginas ·{" "}
            {pdf.cuadrante.id} · huella <span className="mono">{corto(pdf.huella)}</span>
          </p>
        )}
      </section>

      {/* EL ANCLAJE (§8.3). Es lo único que le quita el «PROVISIONAL»: deja la huella y su fecha en
          un registro que cualquiera puede consultar sin cuenta. No publica la posición —eso sigue
          siendo del autor—, así que anclar no es enseñar. */}
      <section className="carta">
        <h2>Anclar el certificado<span className="sub">{anclado ? "anclado" : "lo que le quita el «provisional»"}</span></h2>
        {anclado ? (
          <>
            <p className="vacio">
              Anclado el {fecha(anclado.anclado)}{anclado.repetido ? " (ya lo estaba: la fecha de un anclaje no se cambia)" : ""}.
              Cualquiera puede comprobar en esa dirección que un certificado con esta huella existía ese día.
              Tu posición no se publica: eso lo enseñas tú si quieres.
            </p>
            <p className="vacio">
              Definitivo: <span className="mono">{anclado.ruta}</span> · se comprueba en{" "}
              <a href={anclado.url} target="_blank" rel="noreferrer"><span className="mono">{anclado.url}</span></a>
            </p>
            <div className="dir">
              <a className="btn" href={`data:application/pdf;base64,${anclado.pdf_base64}`} download={nombreDeRuta(anclado.ruta)}>
                Descargar el definitivo
              </a>
            </div>
          </>
        ) : (
          <>
            <p className="vacio">
              Al anclarlo, la huella de tu certificado y su fecha quedan en verify.humanink.io, y el PDF sale
              ya sin «PROVISIONAL», con un QR para comprobarlo. <b>De tu libro no viaja nada</b>: ni el título,
              ni la posición, ni una palabra del texto — solo la huella.
            </p>
            <div className="dir">
              <button className="btn" disabled={ocupado || pos.proyecto.estado !== "activo"}
                      onClick={() => hacer(async () => {
                        const clerk = await getToken();
                        if (!clerk) throw new Error("Tu sesión ha caducado. Vuelve a entrar.");
                        setAnclado(await anclarCertificado({ dir: carpeta, doc, clerk }));
                      })}>
                Anclarlo y emitir el definitivo
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );
}
